import { useSelector, useReceiptGenerator, ReceiptType, useLogger, useI18n } from '~/Hooks';
import { useStorage } from '~Components/Storage';
import { useDeviceHandler } from '~Components/DeviceProvider';
import { PrintRow } from '~Components/PrinterManager';
import { useApolloClient } from '@apollo/client';
import { sortBy, sumBy } from 'lodash';
import { groupOfferMatches } from '~Utils/offers';

const usePrintReceipt = ({ venueId }: { venueId: string }) => {
  const client = useApolloClient();
  const { i18n } = useI18n();
  const print = useSelector((state) => state.POSReceiptPrints);
  const POSOrder = useSelector((state) => print && state.POSOrders.find((o) => o.id === print.id));
  const POSPayment = useSelector((state) =>
    state.POSPayments.find((p) => p.order === POSOrder?.id),
  );

  // If we sync the order before printing, POSPayment and POSOrder will be null, so we need to use the print state.
  const payment = POSPayment || print?.payment;
  const order = POSOrder || print?.order;

  const flags = useSelector((state) => state.companyFlags);
  const [storage] = useStorage();

  const country = useSelector((state) => state.country);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const receiptCounter = useSelector((state) => state.POSReceiptCounter);
  const deviceHandler = useDeviceHandler();
  const splitOrder = useSelector((state) => state.POSSplitOrder);
  const companyFlags = useSelector((state) => state.companyFlags);

  const generateReceipt = useReceiptGenerator({ venueId });
  const logger = useLogger('receipt-printer');

  const printReceipt = async () => {
    logger.info('ReceiptPrinter: Started');

    if (!print) {
      logger.info('ReceiptPrinter: No print data found');
      return;
    }

    logger.info('ReceiptPrinter: Initiating', {
      information: { print, order: order, payment: payment },
    });

    const orderSubPayments = order?.subPayments;
    const printSubPayments = print?.order?.subPayments;
    const splitPayments = splitOrder?.payments;

    const multiplePayments = orderSubPayments?.length
      ? orderSubPayments
      : printSubPayments?.length
      ? printSubPayments
      : splitPayments?.length
      ? splitPayments
      : [];

    // Add tips from subpayments if they exists
    let tip = 0;
    if (multiplePayments.length) {
      tip = multiplePayments.reduce((sum, p) => (sum += ('tip' in p ? p.tip : 0) || 0), tip);
    } else {
      tip = payment?.meta?.tip || 0;
    }

    let receiptNumber;

    if (country === 'ES' && companyFlags.useNewLicenses && !!order?.userId) {
      receiptNumber = `FO-${deviceHandler?.activeDevice?.name || ''}-${receiptCounter}`;
    } else if (country === 'ES' && companyFlags.useNewLicenses) {
      receiptNumber = `${deviceHandler?.activeDevice?.name || ''}-${receiptCounter}`;
    } else {
      receiptNumber = receiptCounter?.toString() ?? '1';
    }

    const extraVars = {
      checkId: receiptNumber,
      deviceName: storage.taxComplianceDeviceName || '',
      deviceId: storage.taxComplianceControlUnitSerial || '',
      tip: tip.toFixed(2),
      deviceRegNumber: storage.taxComplianceProductionNumber || '',
      orderNumber: order?.orderNumber,
      paymentIncTip: '0', // Initialize as string to match later formats
    };

    // Initialize paymentIncTip in cents
    let paymentIncTip = 0;

    if (print.id && order) {
      // Group items by offers to show orange grouped headers
      // Uses Tables-specific utility that works directly with TableItem (no conversion needed)
      const grouped = storage.receiptShowOnlyOfferName
        ? groupOfferMatches(order.items, venueId, client, i18n)
        : { matches: [], unmatched: order.items };

      const items = grouped.unmatched.map((item) => ({
        entryId: item.entryId,
        amount: item.amount,
        discountTitle: item.discountTitle,
        discount: item.discount,
        fraction: item.fraction,
        percentageVAT: item.vatRate,
        variations: item.variations.map((variation) => variation.name),
        title: item.manualName ? item.message! : item.title,
      }));

      const offerItems = grouped.matches.reduce((acc, offer) => {
        const totalPrice = offer.items.reduce((sum, item) => sum + item.amount, 0);
        const offerDiscount = offer.items.reduce((sum, item) => sum + item.discount, 0);

        const offerHeader = {
          entryId: `offer-${offer.offerId}`,
          amount: totalPrice,
          discount: offerDiscount,
          discountTitle: i18n('Receipt.Discount'), // Since the offer name is shown in the title we just add the generic "Discount" text here to avoid redundancy
          percentageVAT: offer.items[0].vatRate,
          variations: [],
          title: offer.offerName,
          fraction: 1,
        };

        return [...acc, offerHeader];
      }, [] as typeof items);

      const total = sumBy([...items, ...offerItems], (item) => item.amount - item.discount);
      const totalDiscount = sumBy([...items, ...offerItems], (item) => item.discount);

      /** Case: Non-split payments (Both TSR & QSR) */
      if (!order?.subPayments || !splitOrder?.payments) {
        paymentIncTip = payment?.amount || total;
      }

      const hasQSRSplitPayments = splitOrder && splitOrder?.payments.length > 0;

      const payments: {
        type: string;
        amount: string;
        transactionId?: string;
        tip?: string;
      }[] = [];

      // In the QSR split payment all payments (including the main one) are included in splitOrder.payments, so we should avoid adding the main payment twice. In TSR and non-split payments we use the main payment info.
      if (!hasQSRSplitPayments) {
        const totAmount = payment?.amount ?? total;
        const tipAmount = payment?.meta?.tip || 0;

        payments.push({
          type:
            order?.method === 'TIMEOUT'
              ? order?.method
              : order?.method === 'IZETTLE'
              ? 'Credit Card'
              : paymentMethods.find((method) => order?.method === method.id)?.id || 'Credit Card',
          amount: ((totAmount + tipAmount) / 100).toFixed(2),
          transactionId: payment?.meta.transactionId,
          tip: tipAmount ? (tipAmount / 100).toFixed(2) : undefined,
        });
      }

      // Add subpayments amounts (in cents) to the total
      multiplePayments.forEach((pm) => {
        const isSubPayment = 'type' in pm;

        payments.push({
          type: isSubPayment ? pm.type : pm.methodTitle,
          amount: (pm.amount / 100 + ('tip' in pm && pm.tip ? pm.tip / 100 : 0)).toFixed(2),
          transactionId: isSubPayment ? pm.transactionId : pm.meta.transactionId,
          tip:
            'tip' in pm && pm.tip ? (pm.tip ? (pm.tip / 100).toFixed(2) : undefined) : undefined,
        });

        paymentIncTip += pm.amount;
      });

      // Format final amount with tip
      extraVars.paymentIncTip = (paymentIncTip + tip).toFixed(2);

      const sortedItems = sortBy([...offerItems, ...items], (item) => item.title.toLowerCase());

      const receipt = await generateReceipt({
        items: sortedItems,
        totalDiscount,
        type: ReceiptType.Sale,
        payments,
        cardDetails: payment?.meta?.cardDetails,
        extraVars: {
          ...extraVars,
          itemsTotal: (total / 100).toFixed(2),
        },
        userId: country === 'ES' ? order?.userId ?? undefined : undefined,
      });

      // Find transactionId from payment, if it doesn't exist, find it from print state.
      const transactionIdForQRCode =
        payments.find((p) => p.transactionId)?.transactionId || payment?.meta.transactionId;

      if (receipt) {
        const message: PrintRow[] = receipt.lines.map((line) => ({
          fontSize: line.fontSize || (line.doubleHeigth || line.doubleWidth ? 26 : 13),
          content: line.text,
          alignment: line.align,
        }));

        const QR =
          flags.hasPoints && !storage.printQROnCheque
            ? `claim:${transactionIdForQRCode}:${total}:org_8AGDOueNZxuxTJ0PfaZk5`
            : undefined;

        if (QR) {
          message.push({
            content: QR,
            qr: true,
          });
        }

        logger.info('ReceiptPrinter: Sending to printer manager', {
          information: {
            receipt,
            print,
          },
        });

        window.printerManager?.initiatePrint({
          data: message,
          printType: 'RECEIPT',
          base64Image: receipt.base64Image,
        });
      } else {
        logger.error('ReceiptPrinter: Print was dismissed', {
          information: {
            receipt,
            terminal: print?.terminal,
          },
        });
      }
    }
  };

  return { printReceipt };
};

export default usePrintReceipt;
